import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Mail, Building2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/SEO/SEOHead';

export function MentionsLegalesPage() {
  const { locale } = useParams<{ locale: string }>();
  const { t, i18n } = useTranslation('mentionsLegales');
  const currentLocale = locale || i18n.language || 'fr';

  const sections = t('sections', { returnObjects: true }) as any[];

  const getIcon = (iconName?: string) => {
    switch(iconName) {
      case 'building': return <Building2 className="w-6 h-6 text-aspexit-green" />;
      case 'shield': return <Shield className="w-6 h-6 text-aspexit-green" />;
      case 'mail': return <Mail className="w-6 h-6 text-aspexit-green" />;
      default: return null;
    }
  };

  return (
    <div>
      <SEOHead
        title={t('title')}
        description={t('subtitle')}
        path="/mentions-legales"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-aspexit-gray">
            <Link to={`/${currentLocale}`} className="hover:text-aspexit-green">
              {t('breadcrumb.home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-aspexit-black">{t('breadcrumb.current')}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-heading font-extrabold text-aspexit-black mb-6">
              {t('title')}
            </h1>
            <p className="text-aspexit-gray">
              {t('subtitle')}
            </p>
          </div>

          {/* Legal Content */}
          <div className="prose prose-lg max-w-none">
            {sections.map((section: any) => (
              <section key={section.id} className="mb-12">
                {/* Section Header */}
                {section.icon ? (
                  <div className="flex items-center gap-3 mb-4">
                    {getIcon(section.icon)}
                    <h2 className="text-2xl font-heading font-bold text-aspexit-black m-0">
                      {section.number}. {section.title}
                    </h2>
                  </div>
                ) : (
                  <h2 className="text-2xl font-heading font-extrabold text-aspexit-black mb-6">
                    {section.number}. {section.title}
                  </h2>
                )}

                {/* Content with key-value pairs (editeur, hebergement) */}
                {section.content && Array.isArray(section.content) && section.content[0]?.label && (
                  <div className={section.id === 'editeur' ? "bg-aspexit-gray-light rounded-lg p-6 border-l-4 border-aspexit-green" : "bg-aspexit-gray-light rounded-lg p-6"}>
                    {section.content.map((item: any, idx: number) => (
                      <p key={idx} className={idx === section.content.length - 1 ? "mb-0" : "mb-2"}>
                        <strong>{item.label}</strong> {item.value}
                      </p>
                    ))}
                  </div>
                )}

                {/* Paragraphs */}
                {section.paragraphs && section.paragraphs.map((para: string, idx: number) => (
                  <p key={idx} className="text-aspexit-gray mb-4">
                    {para}
                  </p>
                ))}

                {/* Contact Methods (for section 5) */}
                {section.contactMethods && (
                  <ul className="list-disc pl-6 text-aspexit-gray mb-4">
                    {section.contactMethods.map((method: string, idx: number) => (
                      <li key={idx}>{method}</li>
                    ))}
                  </ul>
                )}

                {/* Link to Privacy Policy (section 5) */}
                {section.linkText && (
                  <p className="text-aspexit-gray">
                    {section.linkText}{' '}
                    <Link
                      to={`/${currentLocale}${section.linkUrl}`}
                      className="text-aspexit-green hover:underline"
                    >
                      {section.linkLabel}
                    </Link>.
                  </p>
                )}

                {/* Contact Form Link (section 9) */}
                {section.contactLabel && (
                  <div className="bg-aspexit-gray-light rounded-lg p-6">
                    <p className="mb-0">
                      <strong>{section.contactLabel}</strong>{' '}
                      <Link
                        to={`/${currentLocale}${section.contactLinkUrl}`}
                        className="text-aspexit-green hover:underline"
                      >
                        {section.contactLinkText}
                      </Link>
                    </p>
                  </div>
                )}
              </section>
            ))}

            {/* Date de mise à jour */}
            <section>
              <p className="text-sm text-aspexit-gray italic">
                Dernière mise à jour : {t('lastUpdate')}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
